"use client"

import type React from "react"
import { useMemo } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";

import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";

import type { TreeNode, FolderStatus, FileStatus } from "../../file-tree-manager/types/tree-types"
import { highlightSearchTerm } from "../../file-tree-manager/utils/search-utils"
import { getFolderStatusInfo, getFileStatusInfo } from "../../file-tree-manager/utils/status-utils"

interface TreeItemProps {
  node: TreeNode
  level: number
  selectedNode: TreeNode | null
  editingId: string | null
  editingName: string
  treeData: TreeNode[]
  searchTerm?: string
  onNodeSelect: (node: TreeNode) => void
  onContextMenu: (event: React.MouseEvent, nodeId: string) => void
  onToggleExpanded: (id: string) => void
  onEditingNameChange: (name: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
}

export const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  selectedNode,
  editingId,
  editingName,
  treeData,
  searchTerm = "",
  onNodeSelect,
  onContextMenu,
  onToggleExpanded,
  onEditingNameChange,
  onSaveEdit,
  onCancelEdit,
}) => {
  const isEditing = editingId === node.id
  const isSelected = selectedNode?.id === node.id

  // 상태 정보 가져오기
  const statusInfo = useMemo(() => {
    if (!node.status) return null;
    return node.type === "folder"
      ? getFolderStatusInfo(node.status as FolderStatus)
      : getFileStatusInfo(node.status as FileStatus);
  }, [node.status, node.type]);

  return (
    <Box key={node.id}>
      <ListItemButton
        sx={{
          py: 0,
          ml: level * 2,
          backgroundColor: isSelected ? "action.selected" : "transparent",
          opacity: 1,
          border: "2px solid transparent",
          borderColor: "transparent",
          cursor: isEditing ? "text" : "grab",
          "&:hover": {
            backgroundColor: isSelected ? "action.selected" : "action.hover",
          },
          "&:active": {
            cursor: "grabbing",
          },
          transition: "all 0.2s ease",
        }}
        onClick={() => onNodeSelect(node)}
        onContextMenu={(e) => onContextMenu(e, node.id)}
      >
        {node.type === "folder" && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpanded(node.id)
            }}
            sx={{ mr: 0 }}
          >
            {node.isExpanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        )}

        <ListItemIcon sx={{ minWidth: 32 }}>
          {node.type === "folder" ? (
            <FolderOutlined sx={{ color: "primary.main" }} />
          ) : (
            <InsertDriveFileOutlined sx={{ color: "text.secondary" }} />
          )}
        </ListItemIcon>

        {isEditing ? (
          <TextField
            size="small"
            value={editingName}
            onChange={(e) => onEditingNameChange(e.target.value)}
            onBlur={onSaveEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit()
              if (e.key === "Escape") onCancelEdit()
            }}
            autoFocus
            sx={{ flexGrow: 1 }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <ListItemText
            sx={{ minWidth: 0 }}
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0, fontSize: "0.8rem", minWidth: 0 }}>
                {statusInfo && (
                  <Box
                    sx={{
                      px: 0.5,
                      mr: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      backgroundColor: statusInfo.bgColor,
                      fontSize: "0.75rem",
                      fontWeight: "medium",
                      color: statusInfo.color,
                      lineHeight: 1,
                    }}
                  >
                    {statusInfo.label}
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {highlightSearchTerm(node.title, searchTerm)}
                </Box>
              </Box>
            }
            // primaryTypographyProps={{
            //   variant: "body2",
            //   sx: {
            //     fontWeight: isSelected ? "medium" : "inherit",
            //     color: "inherit",
            //   },
            //   noWrap: true,
            // }}
          />
        )}
      </ListItemButton>

      {node.type === "folder" && node.isExpanded && node.children && (
        <Collapse in={node.isExpanded} timeout="auto" unmountOnExit>
          <List dense component="div" disablePadding>
            {node.children.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                level={level + 3}
                selectedNode={selectedNode}
                editingId={editingId}
                editingName={editingName}
                treeData={treeData}
                searchTerm={searchTerm}
                onNodeSelect={onNodeSelect}
                onContextMenu={onContextMenu}
                onToggleExpanded={onToggleExpanded}
                onEditingNameChange={onEditingNameChange}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  )
}


